import React, { Component, type ChangeEvent, type ReactNode } from 'react';
import { Progress, Alert } from 'reactstrap';
import axios, { type AxiosProgressEvent, type AxiosRequestConfig } from 'axios';

import qs from 'qs';
import _ from 'lodash';
import CryptoJS from 'crypto-js';
import withFormsyValidations from '../withFormsyValidations';
import { getAxiosInstance } from '../../utils/appWrapper';

interface SignedUrlResponse {
  signedUrl: string;
  url: string;
}

interface FileUploaderProps {
  prefix: string;
  onChange: (name: string, value: string) => void;
  name: string;
  setValue: (value: string) => void;
  value?: string;
  errorMessage?: string;
  isValid: boolean;
}

interface FileUploaderState {
  progress: number;
  file: string | undefined;
  modal: boolean;
}

const bucketUrl: string = 'flaivy-assets.s3.amazonaws.com';
const cloudfrontBucketUrl: string = 'assets.flaivy.com';

function getMd5Sum(file: File): Promise<string> {
  const reader = new FileReader();
  reader.readAsBinaryString(file);

  return new Promise((resolve, reject) => {
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const contents = e.target?.result;
      const hash = CryptoJS.MD5(
        CryptoJS.enc.Latin1.parse(contents as string),
      ).toString();
      if (hash.length !== 32) {
        /* eslint-disable-next-line no-console */
        console.error('Failed to upload image');
        reject(e);
      }
      resolve(hash);
    };
    reader.onerror = (e: ProgressEvent<FileReader>) => {
      reject(e);
    };
  });
}

class FileUploader extends Component<FileUploaderProps, FileUploaderState> {
  uploader: React.RefObject<HTMLInputElement>;

  static defaultProps = {
    value: undefined,
    errorMessage: '',
  };

  prefix: string;

  constructor(props: FileUploaderProps) {
    super(props);
    this.state = { progress: 0, file: props.value, modal: false };
    this.uploader = React.createRef();
    this.prefix = props.prefix;
  }

  onProgress = (event: AxiosProgressEvent): void => {
    const progress = _.round((100 * event.loaded) / (event.total || 1));
    this.setState({ progress });
  };

  onFinish = ({ url }: { url: string }): void => {
    const { name, onChange, setValue } = this.props;
    const cloudFrontUrl = url.replace(bucketUrl, cloudfrontBucketUrl);
    this.setState({ file: cloudFrontUrl, modal: false });
    setValue(cloudFrontUrl);
    onChange(name, cloudFrontUrl);
  };

  async getSignedUrl(file: File): Promise<SignedUrlResponse> {
    const hash = await getMd5Sum(file);
    const fileEnding = file.type.split('/')[1];
    const queryStr = qs.stringify({
      fileName: `${hash}.${fileEnding}`,
      fileType: file.type,
    });
    const response = await getAxiosInstance({}).get<SignedUrlResponse>(
      `${this.prefix || ''}/sign-s3?${queryStr}`,
    );
    return response.data;
  }

  handleRawUpload = async (file: File): Promise<void> => {
    const signedUrlObj = await this.getSignedUrl(file);
    await this.uploadFile(signedUrlObj.signedUrl, file);
    this.onFinish(signedUrlObj);
  };

  uploadFile(signedUrl: string, file: File): Promise<any> {
    const options: AxiosRequestConfig = {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) =>
        this.onProgress(progressEvent),
    };
    // must be an unintercepted axios call here
    return axios.put(signedUrl, file, options);
  }

  toggle = (): void => {
    this.setState(prevState => ({
      modal: !prevState.modal,
    }));
  };

  override render(): ReactNode {
    const { file, progress } = this.state;

    const { errorMessage } = this.props;

    return (
      <>
        <div>
          <input type="text" readOnly src={file} />
          <input
            id="image-file"
            type="file"
            onChange={async (e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.files && e.target.files[0]) {
                await this.handleRawUpload(e.target.files[0]);
              }
            }}
          />
          {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
          {progress !== 0 && progress !== 100 && (
            <Progress value={progress || 0} />
          )}
        </div>
      </>
    );
  }
}

export default withFormsyValidations(FileUploader);
